/* Passkeys: the customer's next phone costs nothing instead of a riyal.
 *
 * WHY THIS EXISTS, IN MONEY
 * =========================
 * An SMS to Qatar costs 0.2634 USD. At a merchant subscription near QAR 150 a
 * month, about 158 messages consume the whole subscription. So the number that
 * decides the business is not how many customers we have, it is how many ever
 * need a SECOND message — and a passkey is what makes that number small.
 *
 * Phone OTP once, at the counter, where it is the familiar thing. Then a
 * passkey, and after that the session simply persists. A new phone restores it
 * for free, because passkeys sync through iCloud Keychain and Google Password
 * Manager.
 *
 * WHAT "SIGN IN WITH FACE ID" ACTUALLY IS
 * =======================================
 * There is no Face ID API on the web and there does not need to be. A passkey
 * is a WebAuthn credential held by the platform authenticator, and the gesture
 * that unlocks it is Face ID on iOS and a fingerprint or face on Android. The
 * customer sees Face ID; the standard underneath is WebAuthn.
 *
 * THE SERVER SIDE IS GOTRUE'S MFA FACTORS, AND IT IS VERSION-DEPENDENT
 * ===================================================================
 * Probed against both binaries rather than read:
 *
 *   v2.158.1   400  "factor_type needs to be totp or phone"     the type does not exist
 *   v2.196.0   422  "MFA enroll is disabled for WebAuthn"       a disabled feature
 *   v2.196.0   200  with the two enrol/verify flags set         a factor, with an id
 *
 * So everything here needs GOTRUE_PASSKEY_ENABLED as well as the upgraded auth
 * service. `passkeySupport()` asks the browser whether it can CREATE one;
 * `registerPasskey` and `signInWithPasskey` ask the server, and refuse cleanly
 * rather than half-completing.
 */

export interface PasskeySupport {
  /** The browser has WebAuthn at all. */
  webauthn: boolean;
  /** A built-in authenticator: Face ID, Touch ID, a fingerprint reader. This
   * is the one that matters — a customer with only a USB security key is not
   * who this is for, and offering them "use Face ID" would be a lie. */
  platformAuthenticator: boolean;
}

export async function passkeySupport(): Promise<PasskeySupport> {
  const webauthn =
    typeof globalThis.PublicKeyCredential !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.credentials?.create === "function";
  if (!webauthn) return { webauthn: false, platformAuthenticator: false };
  let platform = false;
  try {
    platform =
      await globalThis.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    // Some browsers throw rather than resolving false. A throw and a false
    // mean the same thing here: do not offer it.
    platform = false;
  }
  return { webauthn: true, platformAuthenticator: platform };
}

/** base64url, which is what WebAuthn speaks and JSON does not. */
function b64urlToBytes(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  // An ArrayBuffer rather than a Uint8Array view. WebAuthn accepts either at
  // runtime; the DOM types insist the view be backed by a plain ArrayBuffer
  // and a Uint8Array is typed as possibly backed by a SharedArrayBuffer, which
  // it never is here.
  const buf = new ArrayBuffer(bin.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return buf;
}

function bytesToB64url(b: ArrayBuffer): string {
  const bytes = new Uint8Array(b);
  let s = "";
  for (const x of bytes) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** WebAuthn hands back ArrayBuffers; the server wants base64url strings. */
function credentialToJson(c: PublicKeyCredential): Record<string, unknown> {
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const out: Record<string, unknown> = {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    response: {} as Record<string, unknown>,
  };
  const res = out.response as Record<string, unknown>;
  if (r.attestationObject) res.attestationObject = bytesToB64url(r.attestationObject);
  if (r.clientDataJSON) res.clientDataJSON = bytesToB64url(r.clientDataJSON);
  if (r.authenticatorData) res.authenticatorData = bytesToB64url(r.authenticatorData);
  if (r.signature) res.signature = bytesToB64url(r.signature);
  if (r.userHandle) res.userHandle = bytesToB64url(r.userHandle);
  return out;
}

export class PasskeyUnsupported extends Error {
  constructor(readonly detail: string) {
    super(`passkeys unavailable: ${detail}`);
    this.name = "PasskeyUnsupported";
  }
}

export class PasskeyDeclined extends Error {
  constructor() {
    super("the customer declined or cancelled");
    this.name = "PasskeyDeclined";
  }
}

/** Turn a browser exception into the one distinction that matters.
 *
 * A CANCELLED GESTURE IS NOT AN ERROR. Somebody who taps "not now" on the
 * system sheet has made a choice, and showing them a failure message for it is
 * the counter's grey "Saved" panel again: a true state rendered as the wrong
 * one. Everything else is a real failure and says so. */
function classify(err: unknown): Error {
  const name = (err as { name?: string })?.name ?? "";
  if (name === "NotAllowedError" || name === "AbortError") return new PasskeyDeclined();
  return new PasskeyUnsupported(name || String(err));
}

export interface PasskeyApi {
  /** POST /factors, returning the enrolment challenge. */
  enrol(friendlyName: string): Promise<Record<string, unknown>>;
  /** POST /factors/{id}/verify with the browser's answer. */
  verify(factorId: string, credential: Record<string, unknown>): Promise<unknown>;
  /** POST /factors/{id}/challenge. */
  challenge(factorId: string): Promise<Record<string, unknown>>;
}

/** Create a passkey for the signed-in customer.
 *
 * Two round trips by construction: the server issues a challenge, the
 * authenticator signs it, the server verifies. Anything that skips the second
 * half has enrolled a factor nobody can authenticate with, which is a screen
 * that ends in a dead end. */
/* WHY THE FUNCTION THAT WAS HERE IS GONE.
 *
 * `enrolPasskey` drove GoTrue's MFA factor endpoints, and it had two problems
 * that are really one problem.
 *
 * It never worked. It read the WebAuthn creation options out of the POST
 * /factors response, and they are not there -- they come from POST
 * /factors/{id}/challenge. So it threw "the auth service returned no creation
 * options" every time, on every device, and the screen rendered "That did not
 * work on this device", which named a cause nobody had tested.
 *
 * And it would not have been worth fixing. A factor binds to a session that
 * already exists, so the best case was a credential that could not sign
 * anybody in -- the thing the founder ruled must not appear on a chooser: a
 * Face ID button that unlocks a session rather than creating one.
 *
 * What follows uses the passkey endpoints instead, which are a different
 * feature and were switched off until the flag was found.
 */

/** Bind a passkey to the signed-in account, so the NEXT sign-in needs no code. */
export async function registerPasskey(api: PasskeyRegisterApi): Promise<void> {
  const support = await passkeySupport();
  if (!support.webauthn) throw new PasskeyUnsupported("no webauthn");
  if (!support.platformAuthenticator) throw new PasskeyUnsupported("no platform authenticator");

  const started = await api.options();
  const challengeId = String(started.challenge_id ?? "");
  const publicKey = (started.options ?? {}) as PublicKeyCredentialCreationOptions;
  if (!challengeId || !publicKey.challenge) {
    throw new PasskeyUnsupported("the auth service returned no creation options");
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: {
        ...publicKey,
        challenge: b64urlToBytes(publicKey.challenge as unknown as string),
        user: {
          ...publicKey.user,
          id: b64urlToBytes(publicKey.user.id as unknown as string),
        },
      },
    })) as PublicKeyCredential;
  } catch (err) {
    throw classify(err);
  }
  if (!credential) throw new PasskeyDeclined();

  await api.verify(challengeId, credentialToJson(credential));
}

/** Sign in with a passkey, from signed out.
 *
 * NO IDENTIFIER IS PASSED, and that is the whole feature rather than an
 * omission: the credential is discoverable (`residentKey: required` at
 * registration), so the authenticator knows which account it is and the
 * customer types nothing at all. */
export async function signInWithPasskey<S>(api: PasskeySignInApi<S>): Promise<S> {
  // NOT passkeySupport(). That asks whether this device can CREATE a
  // credential -- it requires navigator.credentials.create and a built-in
  // authenticator -- and neither is the question here. Signing in needs
  // `get`, and it legitimately succeeds on a laptop with no authenticator of
  // its own when the customer uses their phone. Asking the enrolment question
  // would refuse that customer with "not on this device", which is the exact
  // error this whole change exists to stop us inventing.
  if (
    typeof globalThis.PublicKeyCredential === "undefined" ||
    typeof navigator === "undefined" ||
    typeof navigator.credentials?.get !== "function"
  ) {
    throw new PasskeyUnsupported("no webauthn");
  }

  const started = await api.options();
  const challengeId = String(started.challenge_id ?? "");
  const publicKey = (started.options ?? {}) as PublicKeyCredentialRequestOptions;
  if (!challengeId || !publicKey.challenge) {
    throw new PasskeyUnsupported("the auth service returned no request options");
  }

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: {
        ...publicKey,
        challenge: b64urlToBytes(publicKey.challenge as unknown as string),
      },
    })) as PublicKeyCredential;
  } catch (err) {
    throw classify(err);
  }
  if (!assertion) throw new PasskeyDeclined();

  return api.verify(challengeId, credentialToJson(assertion));
}

export type PasskeyRegisterApi = {
  options(): Promise<Record<string, unknown>>;
  verify(challengeId: string, credential: Record<string, unknown>): Promise<unknown>;
};

export type PasskeySignInApi<S> = {
  options(): Promise<Record<string, unknown>>;
  verify(challengeId: string, credential: Record<string, unknown>): Promise<S>;
};
